import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Clock, CalendarDays, User, Mail, Phone, XCircle, RefreshCw } from "lucide-react";
import { format, isBefore, startOfToday } from "date-fns";

export default function ManageBooking() {
  const { token } = useParams();
  const [appointment, setAppointment] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [rescheduleMode, setRescheduleMode] = useState(false);
  const [newDate, setNewDate] = useState(null);
  const [newSlot, setNewSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/bookings/${token}`);
        if (res.data.appointment) {
          setAppointment(res.data.appointment);
          setSettings(res.data.settings || {});
        }
      } catch (e) {  }
      setLoading(false);
    };
    load();
  }, [token]);

  const fetchSlots = async (date) => {
    if (!date || !appointment) return;
    setLoadingSlots(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const res = await axios.get(`${API}/availability?date=${dateStr}&duration_minutes=${appointment.duration_minutes}`);
      setAvailableSlots(res.data.slots || []);
    } catch (e) { setAvailableSlots([]); }
    setLoadingSlots(false);
  };

  useEffect(() => {
    if (newDate) fetchSlots(newDate);
  }, [newDate]);

  const handleReschedule = async () => {
    if (!newSlot) return;
    try {
      const res = await axios.put(`${API}/bookings/${token}/reschedule`, {
        new_datetime: newSlot.datetime,
        staff_id: newSlot.staff_id
      });
      if (res.data.success) {
        setAppointment(res.data.appointment);
        setRescheduleMode(false);
        toast.success("Booking rescheduled!");
      }
    } catch (e) { toast.error("Failed to reschedule"); }
  };

  const [cancelCheck, setCancelCheck] = useState(null);
  const [checkingCancel, setCheckingCancel] = useState(false);

  const handleCancel = async () => {
    try {
      const res = await axios.put(`${API}/bookings/${token}/cancel`);
      if (res.data.success) {
        setAppointment(prev => ({ ...prev, booking_status: 'Cancelled' }));
        toast.success("Booking cancelled");
        setCancelCheck(null);
      }
    } catch (e) { toast.error("Failed to cancel"); }
  };

  const checkBeforeCancel = async () => {
    setCheckingCancel(true);
    try {
      const res = await axios.get(`${API}/bookings/${token}/cancel-check`);
      setCancelCheck(res.data);
    } catch (e) {
      setCancelCheck({ is_late_cancellation: false });
    }
    setCheckingCancel(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;
  if (!appointment) return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="p-8 rounded-2xl text-center max-w-md">
        <h2 className="text-xl font-semibold mb-2">Booking not found</h2>
        <p className="text-muted-foreground text-sm mb-4">This booking link may have expired or is invalid.</p>
        <Link to="/"><Button className="rounded-xl">Back to home</Button></Link>
      </Card>
    </div>
  );

  const apptDate = new Date(appointment.appointment_datetime);
  const statusColors = {
    Confirmed: 'bg-green-100 text-green-700',
    Pending: 'bg-yellow-100 text-yellow-700',
    Completed: 'bg-blue-100 text-blue-700',
    Cancelled: 'bg-red-100 text-red-700',
    'No-show': 'bg-gray-100 text-gray-700'
  };

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-center" />
      <header className="border-b border-border bg-white">
        <div className="max-w-2xl mx-auto px-4 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2">
            {(settings.icon_logo_url || settings.logo_url) ? (
              <img src={settings.icon_logo_url || settings.logo_url} alt={settings.salon_name || 'Salon'} className="w-8 h-8 rounded-lg object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                {((settings.salon_name || 'B')[0]).toUpperCase()}
              </div>
            )}
            <span className="font-semibold text-sm" style={{ fontFamily: 'Playfair Display' }}>{settings.salon_name || ''}</span>
          </Link>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-semibold tracking-tight mb-1" style={{ fontFamily: 'Playfair Display' }}>Manage your booking</h1>
        <p className="text-sm text-muted-foreground mb-6">Ref: {appointment.booking_id}</p>

        <Card className="p-6 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)] mb-6">
          <div className="flex items-center justify-between mb-4">
            <Badge className={`${statusColors[appointment.booking_status] || 'bg-gray-100'} text-xs font-medium rounded-full px-3 py-1`}>
              {appointment.booking_status}
            </Badge>
            <a href={`${API}/bookings/${token}/ics`} download className="text-xs text-primary hover:underline">Download calendar file</a>
          </div>
          
          <div className="space-y-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Services</p>
              {(appointment.services || []).map((s, i) => (
                <div key={s.name || i} className="flex justify-between py-1">
                  <span className="text-sm">{s.name}</span>
                  <span className="text-sm text-muted-foreground">£{s.price?.toFixed(2)}</span>
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-2">
                <CalendarDays size={16} className="text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">{format(apptDate, 'EEE, d MMM yyyy')}</p>
                  <p className="text-muted-foreground">{format(apptDate, 'HH:mm')}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock size={16} className="text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">{appointment.duration_minutes} minutes</p>
                  <p className="text-muted-foreground">Total: £{appointment.total_price?.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Reschedule section */}
        {rescheduleMode && (
          <Card className="p-6 rounded-2xl border mb-6 fade-in">
            <h3 className="font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>Reschedule</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Calendar mode="single" selected={newDate} onSelect={d => { setNewDate(d); setNewSlot(null); }} disabled={(d) => isBefore(d, startOfToday())} />
              <div>
                {loadingSlots ? <div className="animate-pulse">Loading...</div> : (
                  <div className="slot-grid">
                    {availableSlots.map((slot) => (
                      <button key={`${slot.time}-${slot.staff_id}`} onClick={() => setNewSlot(slot)}
                        className={`px-3 py-2 rounded-xl text-sm font-medium transition-colors ${newSlot?.time === slot.time ? 'bg-primary text-white' : 'bg-secondary hover:bg-primary/10'}`}
                      >{slot.time}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleReschedule} disabled={!newSlot} className="rounded-xl">Confirm new time</Button>
              <Button variant="ghost" onClick={() => setRescheduleMode(false)} className="rounded-xl">Cancel</Button>
            </div>
          </Card>
        )}

        {/* Action buttons */}
        {appointment.booking_status !== 'Cancelled' && appointment.booking_status !== 'Completed' && (
          <div className="flex flex-wrap gap-3">
            {!rescheduleMode && (
              <Button variant="secondary" onClick={() => setRescheduleMode(true)} className="rounded-xl">
                <RefreshCw size={16} className="mr-2" /> Reschedule
              </Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" onClick={checkBeforeCancel} disabled={checkingCancel} className="rounded-xl text-destructive border-destructive/30 hover:bg-destructive/5">
                  <XCircle size={16} className="mr-2" /> {checkingCancel ? 'Checking...' : 'Cancel booking'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-2xl">
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {cancelCheck?.is_late_cancellation ? 'Late cancellation warning' : 'Cancel this booking?'}
                  </AlertDialogTitle>
                  <AlertDialogDescription className="space-y-2">
                    {cancelCheck?.is_late_cancellation ? (
                      <>
                        <span className="block text-destructive font-semibold">
                          Your appointment is within {settings.cancellation_window_hours || 24} hours — you will be charged the full amount of £{(cancelCheck.total_price || 0).toFixed(2)}.
                        </span>
                        <span className="block">We recommend rescheduling instead.</span>
                      </>
                    ) : (
                      <span>This action cannot be undone. The time slot will be released.</span>
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                  <AlertDialogCancel className="rounded-xl">Keep booking</AlertDialogCancel>
                  {cancelCheck?.is_late_cancellation && (
                    <AlertDialogCancel onClick={() => { setCancelCheck(null); setRescheduleMode(true); }} className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90">
                      Reschedule instead
                    </AlertDialogCancel>
                  )}
                  <AlertDialogAction onClick={handleCancel} className="rounded-xl bg-destructive text-destructive-foreground">
                    {cancelCheck?.is_late_cancellation ? 'Cancel anyway' : 'Yes, cancel'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>
    </div>
  );
}
